import React, { useEffect, useState } from "react";
import {
  Gift, X, Ticket, LayoutGrid, Coins, Ban, Crown, Lock, Circle, Check, ExternalLink,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const ICONS = { Ticket, LayoutGrid, Coins, Ban, Crown, Circle };
const DISCORD_URL = process.env.REACT_APP_DISCORD_URL || "https://discord.gg/taskscape";

// Fallback ladder shown before an OSRS account is added (mirrors backend rewards.py).
const DEFAULT_LADDER = [
  { id: "free_skip", name: "Extra Monthly Skip", icon: "Ticket", threshold: 3, desc: "Earn 1 free task skip every month, on top of any supporter skips." },
  { id: "slot_2", name: "Task Slot II", icon: "LayoutGrid", threshold: 10, desc: "Hold 2 tasks at once — always two different task types to work on in parallel." },
  { id: "cheap_skips", name: "Discount Skips", icon: "Coins", threshold: 25, desc: "Skips get cheaper — 10 points instead of 15." },
  { id: "slot_3", name: "Task Slot III", icon: "LayoutGrid", threshold: 50, desc: "Hold 3 different tasks at once for maximum flexibility." },
  { id: "free_block", name: "Free Category Block", icon: "Ban", threshold: 100, desc: "Block one task type for free — skip the 100-point cost, once." },
  { id: "legend", name: "Legend", icon: "Crown", threshold: 200, desc: "Claim the exclusive Legend role on Discord and wear a gold Legend flair on the leaderboards." },
].map((r) => ({ ...r, earned: false, current: 0, target: r.threshold }));

export default function Rewards({ profileId, refreshKey }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ rewards: DEFAULT_LADDER, earned: 0, total: DEFAULT_LADDER.length, completed: 0, slots: 1 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!profileId) {
      setData({ rewards: DEFAULT_LADDER, earned: 0, total: DEFAULT_LADDER.length, completed: 0, slots: 1 });
      return;
    }
    setLoading(true);
    api
      .get(`/profiles/${profileId}/rewards`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open, profileId, refreshKey]);

  return (
    <>
      <button data-testid="open-rewards" onClick={() => setOpen(true)} className="osrs-btn px-3 py-2 text-sm flex items-center gap-2">
        <Gift size={16} /> <span>Rewards</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" onClick={() => setOpen(false)}>
          <div className="osrs-panel p-6 w-full max-w-2xl max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="rewards-modal">
            <div className="flex items-center justify-between mb-2">
              <h3 className="osrs-title text-2xl flex items-center gap-2">
                <Gift size={22} /> Rewards
                <span className="osrs-num text-[#00FF00] osrs-shadow text-lg" data-testid="rewards-count">[{data.earned}/{data.total}]</span>
              </h3>
              <button onClick={() => setOpen(false)} className="osrs-btn px-2 py-1" data-testid="close-rewards"><X size={16} /></button>
            </div>
            <p className="osrs-shadow text-[#b9a06a] text-sm mb-4">
              Unlock perks by completing tasks. You've completed <span className="osrs-num text-[#FFCF3F]" data-testid="rewards-completed">{data.completed}</span> task{data.completed === 1 ? "" : "s"} —
              currently holding <span className="osrs-num text-[#7CFC7C]" data-testid="rewards-slots">{data.slots}</span> task slot{data.slots === 1 ? "" : "s"}.
            </p>

            {loading ? (
              <p className="osrs-shadow text-[#E6A519] text-center py-8">Reading your rewards...</p>
            ) : (
              <div className="space-y-2" data-testid="rewards-list">
                {data.rewards.map((r) => {
                  const Icon = ICONS[r.icon] || Circle;
                  const pct = Math.round((r.current / r.target) * 100);
                  const isLegend = r.id === "legend";
                  return (
                    <div
                      key={r.id}
                      data-testid={`reward-${r.id}`}
                      className={`flex items-start gap-3 px-3 py-3 border-2 border-black ${r.earned ? (isLegend ? "bg-[#3a2f10]" : "bg-[#2f4a1c]") : "bg-[#241b10]"}`}
                    >
                      <div className={`shrink-0 w-11 h-11 border-2 border-black flex items-center justify-center ${r.earned ? "bg-[#382d1a]" : "bg-black/40"}`}>
                        {r.earned ? (
                          <Icon size={22} className={isLegend ? "text-[#FFD700]" : "text-[#FFCF3F]"} style={{ filter: "drop-shadow(1px 1px 0 #000)" }} />
                        ) : (
                          <Lock size={18} className="text-[#7a6a45]" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={`font-bold osrs-shadow ${r.earned ? (isLegend ? "text-[#FFD700]" : "text-[#FFCF3F]") : "text-[#9a8a5a]"}`}>{r.name}</p>
                          <span className="osrs-num text-[10px] text-[#8a7a54] uppercase">{r.threshold} tasks</span>
                          {r.earned && (
                            <span className="inline-flex items-center gap-0.5 osrs-num text-[10px] text-[#7CFC7C] uppercase">
                              <Check size={11} /> Unlocked
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-[#c9b789] leading-snug mt-0.5">{r.desc}</p>
                        {!r.earned && (
                          <div className="mt-1">
                            <div className="h-2 bg-black border border-[#5a4a2a]">
                              <div className="h-full bg-[#00FF00]" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="osrs-num text-[10px] text-[#E6A519]">{r.current}/{r.target}</span>
                          </div>
                        )}
                        {isLegend && r.earned && (
                          <a
                            data-testid="claim-legend-role"
                            href={DISCORD_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="osrs-btn osrs-btn-green mt-2 px-3 py-1.5 text-xs inline-flex items-center gap-1"
                          >
                            <ExternalLink size={12} /> Claim your Legend role on Discord
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
